import { Fragment, ReactNode, useState } from "react";
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import {
  ChevronDown,
  Flag,
  HandHelping,
  Send,
  Trash2,
} from "lucide-react";

import { Avatar } from "../../components/Avatar/Avatar";
import { Button } from "../../components/Button/Button";
import { Modal } from "../../components/Modal/Modal";
import { Pill } from "../../components/Pill/Pill";
import { useOptionalToast } from "../../components/Toast/toastContext";
import { ApiError, describeError } from "../../lib/errors";
import {
  deleteMessage,
  listFeed,
  listHelpDonations,
  postMessage,
} from "./api";
import type {
  ChatMessage,
  GuildFeedItem,
  HelpDonation,
  HelpRequestItem,
  HelpRequestStatus,
} from "./types";
import { formatMessageTime, isMemberEvent } from "./chatFeedFormat";
import { MemberEventRow } from "./MemberEventRow";
import styles from "./GuildChatFeed.module.css";

interface GuildChatFeedProps {
  projectId: string;
  guildId: string;
  /**
   * `guilds.created_at` — the guild's birthday, drawn as the anchor above the
   * feed once there is nothing older left to load.
   *
   * A prop rather than a field on the feed response, and rather than a query of
   * its own: the page that mounts this card has already read the guild record
   * for its identity header, so the fact is in hand. Putting it on `ListFeed`
   * would repeat one value about the guild on every one of a page's 25 rows.
   */
  createdAt: string;
  /** Page size for history requests. Defaults to 25. */
  pageSize?: number;
}

// === Formatting ====================================================== //


/** "6 min left" / "3 h left" — what an open request still has on the clock. */
function formatTimeLeft(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const sec = Math.floor((d.getTime() - Date.now()) / 1000);
  // An open request past its deadline is real: nothing flips the status until
  // the expiry worker's next pass. Saying "0 min left" would read as a
  // rounding error rather than as the backlog it actually is.
  if (sec <= 0) return "deadline passed";
  const min = Math.floor(sec / 60);
  if (min < 1) return "under 1 min left";
  if (min < 60) return `${min} min left`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} h left`;
  return `${Math.floor(hr / 24)} d left`;
}

/** "a9c1…4f30" — a uuid shortened to both ends, which is what makes two of
 *  them distinguishable at a glance. */
function shortId(id: string): string {
  return id.length <= 12 ? id : `${id.slice(0, 4)}…${id.slice(-4)}`;
}

/** The Pill variant each help-request status is drawn in. */
const STATUS_VARIANT: Record<
  HelpRequestStatus,
  "info" | "success" | "neutral" | "warn"
> = {
  open: "info",
  fulfilled: "success",
  expired: "neutral",
  cancelled: "warn",
};


const STATUS_LABEL: Record<HelpRequestStatus, string> = {
  open: "Open",
  fulfilled: "Fulfilled",
  expired: "Expired",
  cancelled: "Cancelled",
};


/** Reverse a server page (newest-first) into oldest→newest order. */
function oldestFirst(items: GuildFeedItem[]): GuildFeedItem[] {
  return [...items].reverse();
}

/** The feed as this browser tab has it: accumulated pages, oldest page first. */
type LocalFeed = {
  pages: GuildFeedItem[][];
  /** The page index to request on the next "Load older" click. */
  nextPage: number;
  total: number;
};

/**
 * The Chat section of `GuildDetailPage` — ports the Chat card of
 * docs/mockups/guild-detail.html (screen 1 feed + compose, screen 4 delete
 * confirm + empty state) plus the help-request row of the t-68 mockup
 * `guild-chat-help-requests.html` and the member-event row of the t-105 mockup
 * `guild-chat-member-events.html`, both of which live with that card on the
 * board rather than in docs/mockups.
 *
 * The feed is one timeline over two tables: chat messages and the guild's help
 * requests, merged server-side by creation time. It renders oldest→newest with
 * the newest row at the bottom. The server returns each page newest-first, so
 * each page is reversed and pages are accumulated: "Load older" fetches the
 * next (older) page and prepends it, posting appends to the bottom, deleting
 * removes a single row.
 *
 * Three row types come out of it, not two: a chat message carrying
 * `event_action` is not something anybody said but a change in the guild's
 * roster, and is drawn as a MemberEventRow.
 *
 * Help requests are read-only here — no delete, no cancel. Deleting one would
 * take its help_donations with it through ON DELETE CASCADE, erasing the record
 * of resources that were actually handed out; that is a compensation question,
 * not a chat-moderation one.
 */
export function GuildChatFeed({
  projectId,
  guildId,
  createdAt,
  pageSize = 25,
}: GuildChatFeedProps) {
  const toast = useOptionalToast();
  const qc = useQueryClient();

  // Everything the feed has beyond page 1, plus page 1 itself once the feed has
  // been touched: `null` means nothing has happened yet, so the query's page is
  // the whole feed. The first "Load older", post or delete forks a local copy
  // from it — which is what a seeding effect used to do, one render after the
  // rows were already on screen.
  const [local, setLocal] = useState<LocalFeed | null>(null);
  const [loadingOlder, setLoadingOlder] = useState(false);

  const [composeBody, setComposeBody] = useState("");
  const [composeError, setComposeError] = useState<string | null>(null);

  // The message queued for the delete-confirm modal.
  const [pendingDelete, setPendingDelete] = useState<ChatMessage | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // --- Initial load (page 1) ----------------------------------------- //
  const initial = useQuery({
    queryKey: ["guild-messages", projectId, guildId, 1],
    queryFn: () => listFeed(projectId, guildId, { page: 1, limit: pageSize }),
  });

  const seeded = local !== null || initial.data != null;
  const feed: LocalFeed = local ?? {
    pages: initial.data ? [oldestFirst(initial.data.items)] : [],
    nextPage: 2,
    total: initial.data?.total ?? 0,
  };
  const { pages, nextPage, total } = feed;

  /** Fork the local copy from whatever is on screen, then edit it. */
  function editFeed(update: (current: LocalFeed) => LocalFeed) {
    setLocal((prev) => update(prev ?? feed));
  }

  const items = pages.flat();
  const loadedCount = items.length;
  const hasOlder = seeded && loadedCount < total;

  // --- Load older ----------------------------------------------------- //
  async function handleLoadOlder() {
    if (loadingOlder) return;
    setLoadingOlder(true);
    try {
      const res = await listFeed(projectId, guildId, {
        page: nextPage,
        limit: pageSize,
      });
      editFeed((cur) => ({
        // Prepend the older page as a new first entry — feed stays
        // oldest→newest.
        pages: [oldestFirst(res.items), ...cur.pages],
        nextPage: cur.nextPage + 1,
        // `total` is a mixed local/server value: local posts/deletes adjust it
        // optimistically, so a stale server count must never drag it backward
        // (that would make `loadedCount` exceed `total` and hide the feed).
        total: Math.max(cur.total, res.total),
      }));
    } catch (err) {
      toast?.push({
        variant: "danger",
        message:
          err instanceof ApiError
            ? describeError(err.code)
            : describeError("internal"),
      });
    } finally {
      setLoadingOlder(false);
    }
  }

  // --- Compose / post ------------------------------------------------- //
  const postMutation = useMutation({
    mutationFn: (body: string) => postMessage(projectId, guildId, body),
    onSuccess: (msg) => {
      const posted: GuildFeedItem = { type: "message", ...msg };
      // Append to the last (newest) page so the feed grows at the bottom.
      editFeed((cur) => {
        const next = cur.pages.slice();
        if (next.length === 0) next.push([posted]);
        else next[next.length - 1] = [...next[next.length - 1], posted];
        return { ...cur, pages: next, total: cur.total + 1 };
      });
      setComposeBody("");
      setComposeError(null);
      void qc.invalidateQueries({
        queryKey: ["project", projectId, "activity"],
      });
    },
    onError: (err) => {
      setComposeError(
        err instanceof ApiError
          ? describeError(err.code)
          : describeError("internal"),
      );
    },
  });

  function handleSend() {
    const body = composeBody.trim();
    if (!body || postMutation.isPending) return;
    postMutation.mutate(body);
  }

  // --- Delete --------------------------------------------------------- //
  const deleteMutation = useMutation({
    mutationFn: (messageId: string) =>
      deleteMessage(projectId, guildId, messageId),
    onSuccess: (_void, messageId) => {
      editFeed((cur) => ({
        ...cur,
        pages: cur.pages.map((page) =>
          page.filter((it) => it.type !== "message" || it.id !== messageId),
        ),
        total: Math.max(0, cur.total - 1),
      }));
      setPendingDelete(null);
      setDeleteError(null);
      void qc.invalidateQueries({
        queryKey: ["project", projectId, "activity"],
      });
    },
    onError: (err) => {
      setDeleteError(
        err instanceof ApiError
          ? describeError(err.code)
          : describeError("internal"),
      );
    },
  });

  function openDelete(msg: ChatMessage) {
    setDeleteError(null);
    setPendingDelete(msg);
  }

  function closeDelete() {
    if (deleteMutation.isPending) return;
    setDeleteError(null);
    setPendingDelete(null);
  }

  const loading = initial.isLoading && !seeded;
  const hasError = initial.isError && !seeded;
  const isEmpty = seeded && total === 0;
  const initialErrorMessage =
    initial.error instanceof ApiError
      ? describeError(initial.error.code)
      : describeError("internal");

  return (
    <div className={styles.card}>
      <div className={styles.cardHeader}>
        <h3 className={styles.cardTitle}>Chat</h3>
        <span className={styles.cardSub}>
          chat, member events &amp; help requests · newest at the bottom
        </span>
      </div>

      {/* The strip at the top of the card answers one question — "what is above
          this?" — and it always answers it. While pages remain it offers them;
          once there are none it says where the guild began.

          The anchor reads `guilds.created_at`, so it stands over ANY guild: one
          created before the `created` row existed, and one whose opening the
          retention window has already trimmed. It is deliberately a fact about
          the GUILD and not about the feed — it promises a birthday, not that
          everything since is visible below.

          Which is also why it is what the strip shows while page 1 is still in
          flight, and over a feed that failed to load: the date is known without
          the feed, and the two halves are the same height, so a guild with more
          pages swaps the anchor for the button without moving anything. Leaving
          the strip out until the page lands would push the whole card down as it
          arrived. */}
      {hasOlder ? (
        <div className={styles.chatOlder}>
          <button
            type="button"
            className={styles.link}
            onClick={() => void handleLoadOlder()}
            disabled={loadingOlder}
          >
            ↑ Load older messages
          </button>
        </div>
      ) : (
        <div className={styles.chatAnchor} data-testid="chat-anchor">
          <Flag size={12} className={styles.anchorIcon} aria-hidden="true" />
          <span>Guild created</span>
          <span className={styles.anchorWhen}>
            {formatMessageTime(createdAt)}
          </span>
        </div>
      )}

      <div className={styles.chatFeed} data-testid="chat-feed">
        {loading && (
          <>
            <SkeletonRow />
            <SkeletonRow />
            <SkeletonRow />
          </>
        )}

        {hasError && (
          <div className={styles.empty} role="alert">
            {initialErrorMessage}
          </div>
        )}

        {isEmpty && (
          <div className={styles.empty}>No messages or help requests yet.</div>
        )}

        {!loading &&
          !hasError &&
          !isEmpty &&
          items.map((it) => {
            if (it.type === "help_request") {
              return (
                <HelpRequestRow
                  key={`help:${it.id}`}
                  projectId={projectId}
                  guildId={guildId}
                  item={it}
                />
              );
            }
            if (isMemberEvent(it)) {
              // A roster change, not somebody's words: its own row, and no
              // Delete button on it — see MemberEventRow.
              return <MemberEventRow key={`msg:${it.id}`} msg={it} />;
            }
            return it.kind === "system" ? (
              <SystemMessage
                key={`msg:${it.id}`}
                msg={it}
                onDelete={() => openDelete(it)}
              />
            ) : (
              <PlayerMessage
                key={`msg:${it.id}`}
                msg={it}
                onDelete={() => openDelete(it)}
              />
            );
          })}
      </div>

      <div className={styles.chatCompose}>
        <div className={styles.composeRow}>
          <textarea
            className={styles.composeInput}
            placeholder="Message the guild as an admin…"
            value={composeBody}
            onChange={(e) => setComposeBody(e.target.value)}
            disabled={postMutation.isPending}
          />
          <Button
            variant="primary"
            onClick={handleSend}
            disabled={composeBody.trim().length === 0 || postMutation.isPending}
            loading={postMutation.isPending}
            icon={<Send size={13} aria-hidden="true" />}
          >
            Send
          </Button>
        </div>
        {composeError && (
          <p className={styles.composeError} role="alert">
            {composeError}
          </p>
        )}
        <div className={styles.helper}>
          Posts a <code className={styles.code}>system</code> message — visible
          in-game to every guild member.
        </div>
      </div>

      {pendingDelete && (
        <DeleteMessageModal
          msg={pendingDelete}
          error={deleteError}
          busy={deleteMutation.isPending}
          onClose={closeDelete}
          onConfirm={() => deleteMutation.mutate(pendingDelete.id)}
        />
      )}
    </div>
  );
}

// === Message rows ==================================================== //

/** A player chat message — avatar + name + time + body. Mockup `.msg`. */
function PlayerMessage({
  msg,
  onDelete,
}: {
  msg: ChatMessage;
  onDelete: () => void;
}) {
  const name = msg.sender_name ?? "?";
  return (
    <div className={styles.msg}>
      <span className={styles.avSm}>
        <Avatar name={name} size={30} />
      </span>
      <div className={styles.msgMain}>
        <div className={styles.msgHead}>
          <span className={styles.msgName}>{name}</span>
          <span className={styles.msgTime}>
            {formatMessageTime(msg.created_at)}
          </span>
        </div>
        <div className={styles.msgBody} data-testid="chat-msg-body">
          {msg.body}
        </div>
      </div>
      <button
        type="button"
        className={styles.msgDel}
        aria-label="Delete message"
        title="Delete message"
        onClick={onDelete}
      >
        <Trash2 size={13} aria-hidden="true" />
      </button>
    </div>
  );
}

/** A system message — centered pill with a System tag. Mockup `.sys-msg`. */
function SystemMessage({
  msg,
  onDelete,
}: {
  msg: ChatMessage;
  onDelete: () => void;
}) {
  return (
    <div className={styles.sysMsg}>
      <span className={styles.sysInner}>
        <span className={styles.sysTag}>System</span>
        <span data-testid="chat-msg-body">{msg.body}</span>
      </span>
      <button
        type="button"
        className={styles.msgDel}
        aria-label="Delete message"
        title="Delete message"
        onClick={onDelete}
      >
        <Trash2 size={13} aria-hidden="true" />
      </button>
    </div>
  );
}


// === Help request row ================================================ //

/**
 * One help request in the feed — mockup `.help-row`. Read-only: who asked for
 * what, how far the collection got, and how it ended.
 *
 * Clicking the head expands the card and only then fetches the donors: a page
 * is 25 rows, and carrying the donations of every request on it would be work
 * done for the nine readers out of ten who never open one. Each card holds its
 * own open state and its own query, so they expand independently.
 */
function HelpRequestRow({
  projectId,
  guildId,
  item,
}: {
  projectId: string;
  guildId: string;
  item: HelpRequestItem;
}) {
  const [open, setOpen] = useState(false);
  // Ties the head to the panel it toggles, so a screen reader following
  // aria-expanded has somewhere to go.
  const bodyId = `help-body-${item.id}`;
  const donors = useQuery({
    queryKey: ["guild-help-donations", projectId, guildId, item.id],
    queryFn: () => listHelpDonations(projectId, guildId, item.id),
    enabled: open,
  });

  const name = item.requester_name;
  const statusClass = {
    open: styles.stOpen,
    fulfilled: styles.stFulfilled,
    expired: styles.stExpired,
    cancelled: styles.stCancelled,
  }[item.status];

  // The bar needs something to divide by; without a cap the stats carry the
  // absolute numbers instead and say so.
  const cap = item.max_amount;
  const hasCap = cap !== undefined;
  const pct =
    hasCap && cap > 0
      ? Math.min(100, Math.round((item.current_amount / cap) * 100))
      : 0;
  const meterClass =
    item.status === "fulfilled"
      ? styles.meterDone
      : item.status === "open"
        ? ""
        : styles.meterDead;

  return (
    <div
      className={`${styles.helpRow} ${statusClass}`}
      data-testid="help-request-row"
    >
      <div className={styles.helpGutter}>
        <span className={styles.icoBadge}>
          <HandHelping size={13} aria-hidden="true" />
        </span>
      </div>
      <div className={`${styles.helpCard} ${open ? styles.expanded : ""}`}>
        <button
          type="button"
          className={styles.helpHead}
          aria-expanded={open}
          aria-controls={bodyId}
          onClick={() => setOpen((v) => !v)}
        >
          <span className={styles.helpTitle}>
            <b className={name === null ? styles.helpNameUnknown : undefined}>
              {name ?? "?"}
            </b>{" "}
            asked for <code className={styles.helpType}>{item.request_type}</code>
          </span>
          <Pill variant={STATUS_VARIANT[item.status]} size="sm">
            {STATUS_LABEL[item.status]}
          </Pill>
          <span className={styles.helpTime}>
            {formatMessageTime(item.created_at)}
          </span>
          <span className={styles.helpChev}>
            <ChevronDown
              size={13}
              className={open ? styles.helpChevOpen : undefined}
              aria-hidden="true"
            />
          </span>
        </button>

        <div className={styles.helpMeter}>
          {hasCap && (
            <div className={`${styles.meter} ${meterClass}`} aria-hidden="true">
              <span className={styles.meterFill} style={{ width: `${pct}%` }} />
            </div>
          )}
          <div
            className={`${styles.helpStats} ${hasCap ? "" : styles.helpStatsBare}`}
          >
            {hasCap ? (
              <span>
                <span className={styles.num}>{item.current_amount}</span> /{" "}
                <span className={styles.num}>{cap}</span> collected
              </span>
            ) : (
              <span>
                <span className={styles.num}>{item.current_amount}</span>{" "}
                collected <span className={styles.sep}>· no cap</span>
              </span>
            )}
            <span className={styles.sep}>·</span>
            <DonorCount item={item} />
            <span className={styles.sep}>·</span>
            <span>
              {item.status !== "open" ? (
                // updated_at only moves when the status does, so for a request
                // that is no longer open it is when it closed.
                `closed ${formatMessageTime(item.updated_at)}`
              ) : item.expires_at === null ? (
                // A request of a type whose ttl is ∞. There is no clock to
                // count down, and "deadline passed" — what a missing date would
                // otherwise read as — would be the opposite of the truth.
                "never expires"
              ) : (
                formatTimeLeft(item.expires_at)
              )}
            </span>
          </div>
        </div>

        {open && (
          <div
            className={styles.helpBody}
            id={bodyId}
            data-testid="help-request-body"
          >
            <p className={styles.helpSub}>
              Donations
              {donors.data && donors.data.length > 0 && (
                <Pill size="sm">{donors.data.length}</Pill>
              )}
            </p>
            <DonorList query={donors} />

            <p className={`${styles.helpSub} ${styles.helpSubSpaced}`}>
              Payload
            </p>
            <div className={styles.payload} data-testid="help-payload">
              <PayloadValue value={item.payload} />
            </div>

            <div className={styles.helpIds}>
              <span>
                request id <span className={styles.helpIdValue}>{item.id}</span>
              </span>
              <span>
                requester{" "}
                <span className={styles.helpIdValue}>
                  {shortId(item.requester_id)}
                </span>
              </span>
              <span>
                type{" "}
                <span className={styles.helpIdValue}>{item.request_type}</span>
              </span>
              <span>
                expires{" "}
                <span className={styles.helpIdValue}>
                  {item.expires_at === null
                    ? "never"
                    : formatMessageTime(item.expires_at)}
                </span>
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * "7 donors" / "no donors".
 *
 * A count, not a fraction: nothing caps how many players may chip in — a
 * request is closed by the total they add up to, which the amount beside this
 * already reports. Nobody having chipped in is said in words rather than as a
 * bare 0, because the zero is the answer to the ticket and should not have to
 * be read out of a number.
 */
function DonorCount({ item }: { item: HelpRequestItem }) {
  if (item.current_donors === 0) return <span>no donors</span>;
  return (
    <span>
      <span className={styles.num}>{item.current_donors}</span> donors
    </span>
  );
}

/** The donations list inside an expanded card: skeleton → rows → empty →
 *  failure with a Retry, mockup screen 3. */
function DonorList({ query }: { query: UseQueryResult<HelpDonation[]> }) {
  if (query.isError) {
    return (
      <div className={`${styles.helpEmpty} ${styles.helpError}`} role="alert">
        {query.error instanceof ApiError
          ? describeError(query.error.code)
          : describeError("internal")}{" "}
        <button
          type="button"
          className={`${styles.link} ${styles.retry}`}
          onClick={() => void query.refetch()}
        >
          Retry
        </button>
      </div>
    );
  }
  if (!query.data) {
    return (
      <div data-testid="donors-skeleton">
        <DonorSkeleton width={110} />
        <DonorSkeleton width={86} />
      </div>
    );
  }
  if (query.data.length === 0) {
    return <div className={styles.helpEmpty}>Nobody chipped in.</div>;
  }
  return (
    <>
      {query.data.map((d) => (
        <div className={styles.donor} key={d.donor_id}>
          <Avatar name={d.display_name ?? "?"} size={22} />
          <span className={styles.donorName}>{d.display_name ?? "?"}</span>
          <span className={styles.donorAmount}>+{d.amount}</span>
          <span
            className={`${styles.donorClaim} ${
              d.claimed_amount < d.amount ? styles.donorClaimLeft : ""
            }`}
          >
            claimed {d.claimed_amount} / {d.amount}
            {d.claimed_amount < d.amount &&
              ` · ${d.amount - d.claimed_amount} left`}
          </span>
          <span className={styles.donorTime}>
            {formatMessageTime(d.created_at)}
          </span>
        </div>
      ))}
    </>
  );
}

function DonorSkeleton({ width }: { width: number }) {
  return (
    <div className={styles.donor} aria-hidden="true">
      <span className={`${styles.skel} ${styles.skelAv}`} />
      <span className={styles.skel} style={{ width }} />
      <span className={styles.skel} style={{ width: 34, marginLeft: "auto" }} />
    </div>
  );
}

/**
 * A help request's payload, rendered as one line of JSON with keys, numbers and
 * strings told apart by colour — mockup `.payload`.
 *
 * The payload is whatever the game wrote, so every JSON shape has to render:
 * `JSON.stringify` does the quoting and escaping, and this only decides the
 * colours and the spacing around them.
 */
function PayloadValue({ value }: { value: unknown }): ReactNode {
  if (typeof value === "string") {
    return <span className={styles.payloadStr}>{JSON.stringify(value)}</span>;
  }
  if (typeof value === "number" || typeof value === "boolean" || value === null) {
    return <span className={styles.payloadNum}>{JSON.stringify(value)}</span>;
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return <>[]</>;
    return (
      <>
        {"[ "}
        {value.map((v, i) => (
          <Fragment key={i}>
            {i > 0 && ", "}
            <PayloadValue value={v} />
          </Fragment>
        ))}
        {" ]"}
      </>
    );
  }
  if (typeof value === "object") {
    const entries = Object.entries(value as Record<string, unknown>);
    if (entries.length === 0) return <>{"{}"}</>;
    return (
      <>
        {"{ "}
        {entries.map(([k, v], i) => (
          <Fragment key={k}>
            {i > 0 && ", "}
            <span className={styles.payloadKey}>{JSON.stringify(k)}</span>
            {": "}
            <PayloadValue value={v} />
          </Fragment>
        ))}
        {" }"}
      </>
    );
  }
  // `undefined` — the key was absent from the response altogether.
  return <>—</>;
}

// === Delete-confirm modal ============================================ //

/** Delete-message confirm modal — mockup screen 4. */
function DeleteMessageModal({
  msg,
  error,
  busy,
  onClose,
  onConfirm,
}: {
  msg: ChatMessage;
  error: string | null;
  busy: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) {
  const author = msg.sender_name ?? "System";
  return (
    <Modal
      open
      onClose={onClose}
      title="Delete message?"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={onConfirm}
            disabled={busy}
            loading={busy}
            icon={<Trash2 size={14} aria-hidden="true" />}
          >
            Delete
          </Button>
        </>
      }
    >
      <p className={styles.modalBody}>
        Permanently removes this message from the guild chat —{" "}
        <b>hard delete, no undo</b>.
      </p>
      <div className={styles.modalQuote}>
        {author} — “{msg.body}”
      </div>
      {error && (
        <p className={`${styles.modalBody} ${styles.modalError}`} role="alert">
          {error}
        </p>
      )}
    </Modal>
  );
}

// === Loading skeleton ================================================ //

function SkeletonRow() {
  return (
    <div className={styles.msg} aria-hidden="true">
      <span className={`${styles.avSm} ${styles.skeletonAv}`} />
      <div className={styles.msgMain}>
        <div className={styles.skeletonLineShort} />
        <div className={styles.skeletonLine} />
      </div>
    </div>
  );
}
